import { db } from '../../db/indexeddb'
import {
  getCloudCanvasNotes,
  getCloudCanvasNoteIds,
  getCloudCanvasNotesByIds,
  getDeletedCloudCanvasNotes,
  createCloudCanvasNote,
  updateCloudCanvasNote,
  deleteCloudCanvasNote,
} from '../../supabase/queries'
import { CloudCanvasNoteSchema, CloudCanvasNotePayloadSchema } from '../sync-schemas'
import {
  resolveCloudProjectId,
  resolveLocalProjectId,
  resolveCloudTaskId,
  resolveLocalTaskId,
  resolveCloudCanvasNoteId,
  resolveLocalCanvasNoteId,
} from '../sync-resolve'
import type { PullDescriptor } from '../sync-pull'
import type { PushDescriptor } from '../sync-push'
import type { CanvasNote } from '../../db/models'

/* ── CANVAS NOTES (Projekt-Canvas: Notizen & Memos) ─────────────────────────────────────────── */

export const canvasNotePullDescriptor: PullDescriptor<any> = {
  entityName: 'canvasNote',
  settingsKey: 'lastCanvasNoteSyncTime',

  fetchRemote: (userId, since) => getCloudCanvasNotes(userId, since),
  fetchRemoteIds: (userId) => getCloudCanvasNoteIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudCanvasNotesByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudCanvasNotes(userId, since),

  safeParse: (raw) => CloudCanvasNoteSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.canvasNotes,
  getAllLocal: () => db.canvasNotes.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.canvasNotes.filter(n => !n.deletedAt).count()
    return count === 0
  },

  buildLocalInsert: async (remote) => {
    const localProjectId = await resolveLocalProjectId(remote.project_id)
    // Parent project not on this device yet — the next cycle retries, by which
    // time the project pull (which runs before canvasNotes) will have created it.
    if (!localProjectId) return null
    return {
      id: crypto.randomUUID(),
      projectId: localProjectId,
      kind: remote.kind,
      title: remote.title ?? null,
      content: remote.content,
      x: remote.x,
      y: remote.y,
      width: remote.width,
      height: remote.height,
      // Die **Neigung**. `?? 0` wie überall: Eine Zeile aus einer älteren
      // Fassung kennt das Feld nicht und liegt dann gerade.
      rotation: remote.rotation ?? 0,
      color: remote.color ?? null,
      icon: remote.icon ?? null,
      // `?? false`, nicht `?? undefined`: Eine Zeile aus einer älteren
      // Fassung kennt das Feld nicht und zeigt dann keinen Autor.
      showAuthor: remote.show_author ?? false,
      font: remote.font ?? null,
      fontSize: remote.font_size ?? null,
      textAlign: remote.text_align ?? null,
      // **Vorn und hinten**: Ohne Wert (ältere Zeile) zählt das Alter.
      zOrder: remote.z_order ?? null,
      // Der **Bereich** kommt wie die Verbindung als Cloud- UND als lokale
      // Kennung. Ist er auf diesem Gerät noch nicht da, liegt das Element
      // vorerst frei — der nächste Zyklus trägt es nach.
      areaId: await resolveLocalCanvasNoteId(remote.area_id, remote.area_local_id),
      // Der **Klebestreifen**. `?? null` wie überall: Eine Zeile aus einer
      // älteren Fassung kennt das Feld nicht und klebt dann nicht.
      tape: remote.tape ?? null,
      // Die **Verbindung** kommt als Cloud-Kennung UND als gerätelokale
      // Kennung an; welche trägt, entscheidet der Resolver. Findet er nichts
      // (die Aufgabe ist auf diesem Gerät noch nicht angekommen), bleibt der
      // Notiz ohne Eselsohr — der nächste Zyklus holt es nach. Ein Ohr ohne
      // auffindbare Aufgabe wäre schlimmer als eines, das nachkommt.
      taskId: await resolveLocalTaskId(remote.task_id, remote.task_local_id),
      convertedAt: remote.converted_at ? new Date(remote.converted_at) : null,
      convertedContent: remote.converted_content ?? null,
      userId: remote.user_id,
      cloudId: remote.id,
      deletedAt: null,
      createdAt: new Date(remote.created_at),
      updatedAt: new Date(remote.updated_at),
      lastModified: new Date(remote.updated_at),
      synced: true,
      syncedAt: new Date(),
    }
  },

  buildLocalUpdate: async (remote) => ({
    kind: remote.kind,
    title: remote.title ?? null,
    content: remote.content,
    x: remote.x,
    y: remote.y,
    // Die Höhe einer **Notiz** kommt nicht aus der Cloud: Sie wird auf jedem
    // Gerät gemessen (siehe `setCanvasNoteMeasuredHeight`). Ohne diese
    // Ausnahme überschriebe der Pull die lokale Messung, der ResizeObserver
    // schriebe zurück, der Push trüge es hoch — und das Spiel begänne von
    // vorn. Für den **Notizen** ist sie eine gezogene Größe und gehört mit.
    // Beim **Text** wird auch gemessen (`setCanvasNoteMeasuredSize`), die
    // Breite kommt aber trotzdem mit: Ein zentrierter oder rechtsbündiger Text
    // rückt beim Wachsen sein `x` nach, und das kommt hier schon verschoben an.
    // Mit der alten Breite sähe dieses Gerät das Wachstum noch einmal und
    // verschöbe ein zweites Mal. Hin und her geht nichts: Die Messung schreibt
    // am Sync vorbei.
    width: remote.width,
    ...(remote.kind === 'memo' || remote.kind === 'text' ? {} : { height: remote.height }),
    rotation: remote.rotation ?? 0,
    color: remote.color ?? null,
    icon: remote.icon ?? null,
    // `?? false`, nicht `?? undefined`: Eine Zeile aus einer älteren
    // Fassung kennt das Feld nicht und zeigt dann keinen Autor.
    showAuthor: remote.show_author ?? false,
    font: remote.font ?? null,
    fontSize: remote.font_size ?? null,
    textAlign: remote.text_align ?? null,
    zOrder: remote.z_order ?? null,
    areaId: await resolveLocalCanvasNoteId(remote.area_id, remote.area_local_id),
    tape: remote.tape ?? null,
    taskId: await resolveLocalTaskId(remote.task_id, remote.task_local_id),
    convertedAt: remote.converted_at ? new Date(remote.converted_at) : null,
    convertedContent: remote.converted_content ?? null,
    updatedAt: new Date(remote.updated_at),
    ...(remote.deleted_at ? { deletedAt: new Date(remote.deleted_at) } : { deletedAt: null }),
  }),
}

/**
 * Darf diese Notiz jetzt hochgeladen werden? Nur, wenn sein Projekt — und,
 * falls er **übernommen** wurde, auch seine Aufgabe — schon eine Cloud-Kennung
 * trägt.
 */
async function canPushCanvasNote(note: CanvasNote): Promise<{ defer: boolean }> {
  const cloudProjectId = await resolveCloudProjectId(note.projectId)
  if (!cloudProjectId) return { defer: true }
  if (note.taskId && !(await resolveCloudTaskId(note.taskId))) return { defer: true }
  // Ein Element wartet auf seinen **Bereich**: `area_id` zeigt auf die
  // Cloud-PK derselben Tabelle. Der Bereich selbst hat kein `areaId` und geht
  // darum immer zuerst hoch.
  if (note.areaId && !(await resolveCloudCanvasNoteId(note.areaId))) return { defer: true }
  return { defer: false }
}

export const canvasNotePushDescriptor: PushDescriptor<CanvasNote> = {
  entityName: 'CloudCanvasNote',
  getLocal: async (id) => (await db.canvasNotes.get(id)) ?? null,
  getLocalCloudId: (l) => l.cloudId,
  getLocalId: (l) => l.id,
  table: db.canvasNotes,

  // A canvas note cannot exist in the cloud before its project does; defer
  // until the project push has handed back a cloud id.
  //
  // Dasselbe gilt für die **Verbindung**: `canvas_notes.task_id` zeigt auf die
  // Cloud-PK von `tasks`. Wäre die Aufgabe oben noch nicht angekommen, liefe
  // der Push in einen Foreign-Key-Fehler — oder, mit `null` geschickt, ginge
  // die Verbindung stumm verloren. Deshalb lieber warten.
  //
  // Seit `canvasNotes` in der Push-Reihenfolge hinter `tasks` liegt
  // (sync-engine `tableOrder`), tritt der Fall praktisch nicht mehr ein; das
  // Deferren ist das Netz darunter.
  canCreate: async (note) => canPushCanvasNote(note),
  canUpdate: async (note) => canPushCanvasNote(note),

  buildCreatePayload: async (note) => {
    const cloudProjectId = await resolveCloudProjectId(note.projectId)
    return CloudCanvasNotePayloadSchema.parse({
      project_id: cloudProjectId,
      project_local_id: note.projectId,
      kind: note.kind,
      title: note.title,
      content: note.content,
      x: note.x,
      y: note.y,
      width: note.width,
      height: note.height,
      rotation: note.rotation,
      color: note.color,
      icon: note.icon,
      show_author: note.showAuthor,
      font: note.font,
      font_size: note.fontSize ?? null,
      text_align: note.textAlign ?? null,
      // Die null muss mit: ⌘Z auf „Ganz nach vorn" setzt zurück auf „nach Alter".
      z_order: note.zOrder ?? null,
      area_id: note.areaId ? await resolveCloudCanvasNoteId(note.areaId) : null,
      area_local_id: note.areaId,
      tape: note.tape,
      task_id: note.taskId ? await resolveCloudTaskId(note.taskId) : null,
      task_local_id: note.taskId,
      converted_at: note.convertedAt?.toISOString() ?? null,
      converted_content: note.convertedContent,
      local_id: note.id,
    })
  },

  buildUpdatePayload: async (note) => {
    const cloudProjectId = await resolveCloudProjectId(note.projectId)
    const payload: Record<string, any> = CloudCanvasNotePayloadSchema.parse({
      project_id: cloudProjectId,
      project_local_id: note.projectId,
      kind: note.kind,
      title: note.title,
      content: note.content,
      x: note.x,
      y: note.y,
      width: note.width,
      height: note.height,
      rotation: note.rotation,
      color: note.color,
      icon: note.icon,
      show_author: note.showAuthor,
      font: note.font,
      font_size: note.fontSize ?? null,
      text_align: note.textAlign ?? null,
      // Die null muss mit: ⌘Z auf „Ganz nach vorn" setzt zurück auf „nach Alter".
      z_order: note.zOrder ?? null,
      area_id: note.areaId ? await resolveCloudCanvasNoteId(note.areaId) : null,
      area_local_id: note.areaId,
      tape: note.tape,
      task_id: note.taskId ? await resolveCloudTaskId(note.taskId) : null,
      task_local_id: note.taskId,
      converted_at: note.convertedAt?.toISOString() ?? null,
      converted_content: note.convertedContent,
      local_id: note.id,
    })
    // Die Verbindung kann GELÖST worden sein (die Aufgabe wurde gelöscht) —
    // dann muss die null ausdrücklich mit, sonst behielte die Cloud-Zeile ihr
    // altes `task_id`. Derselbe Grund wie beim `deleted_at` darunter.
    payload.task_id = payload.task_id ?? null
    payload.task_local_id = payload.task_local_id ?? null
    // Dasselbe für den **Bereich**: Wer ihn verlässt, muss die null mitschicken.
    payload.area_id = payload.area_id ?? null
    payload.area_local_id = payload.area_local_id ?? null
    // Und für den **Klebestreifen**: Das **Ablösen** schreibt `null`, und ohne
    // diese Zeile behielte die Cloud-Zeile ihren alten Streifen — der Zettel
    // klebte auf dem anderen Gerät weiter.
    payload.tape = payload.tape ?? null
    // Undo re-activates a soft-deleted row — the null has to be sent explicitly
    // or the cloud row stays dead (sync checklist point 17).
    if (!note.deletedAt) payload.deleted_at = null
    return payload
  },

  createCloud: (userId, payload) => createCloudCanvasNote(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudCanvasNote(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudCanvasNote(cloudId, userId),
}
